import React, { useEffect, useRef } from 'react';
import './BuiltDifferent.css';
import ImagePlaceholder from './ImagePlaceholder';

const PANELS = [
  {
    id: 'recognition',
    label: 'Recognition',
    promise: 'Every regular gets remembered.',
    body:
      "Your dangols open the app and see their progress, their rewards, their relationship with your shop. You see them too \u2014 by name, by visit, by the things that bring them back.",
    contrast: 'Not another anonymous loyalty card',
    video: '/loyalty-walk-in.mp4',
    poster: '/loyalty-walk-in-poster.jpg',
    imagePrompt:
      "A regular walking into the cafe \u2014 silhouette through the door, brass bell trembling, warm morning light pouring in. The familiarity of the daily arrival.",
    imageTag: 'VIDEO \u2014 RECOGNITION',
  },
  {
    id: 'connection',
    label: 'Connection',
    promise: 'A direct line to your community.',
    body:
      "Push messages straight to your dangols\u2019 lock screens \u2014 new menu drops, supply shortages, snowed-in afternoons. No algorithm. No posting and hoping. Just you, talking to your people.",
    contrast: 'Not Instagram (8% reach)',
    video: '/notification.mp4',
    poster: '/notification-poster.jpg',
    imagePrompt:
      'A phone on a wooden cafe table, lock screen lighting up with a notification at the perfect moment.',
    imageTag: 'VIDEO \u2014 CONNECTION',
  },
  {
    id: 'insight',
    label: 'Insight',
    promise: 'Real numbers about real regulars.',
    body:
      "How many dangols do you have? Who\u2019s been slipping away? What brings them back on a slow Tuesday? Most owners are surprised by what they find.",
    contrast: 'Not gut feel',
    video: '/insight.mp4',
    poster: '/insight-poster.jpg',
    imagePrompt:
      'A regular picking up their order \u2014 hands and the moment of the exchange, the rhythm of repeat customers visible in the frame.',
    imageTag: 'VIDEO \u2014 INSIGHT',
  },
];

const BuiltDifferent = () => {
  // Hold refs to each panel video so we can manually trigger play() on mount.
  // iOS Safari sometimes silently ignores autoPlay even with muted+playsInline,
  // so we call play() ourselves and swallow the rejection.
  const videoRefs = useRef({});

  useEffect(() => {
    Object.values(videoRefs.current).forEach((video) => {
      if (!video) return;
      // Force-set muted at the DOM level — iOS Safari sometimes ignores
      // React's JSX `muted` prop, which causes autoplay to fail silently.
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      const result = video.play();
      if (result && typeof result.catch === 'function') {
        result.catch(() => {
          /* Autoplay blocked (Low Power Mode, etc.) — will play on user interaction */
        });
      }
    });
  }, []);

  return (
    <section className="built-different">
      <div className="built-different-intro container-narrow reveal">
        <span className="eyebrow">What makes Dangol different</span>
        <h2 className="built-different-headline display">
          Made for the <em>third places.</em>
        </h2>
        <p className="built-different-sub">
          Loyalty plug-ins track transactions. Dangol is built for the
          neighborhood shops where people belong &mdash; and the three things
          they actually need to grow.
        </p>
      </div>

      <div className="bd-panels reveal">
        {PANELS.map((panel) => {
          return (
            <button
              key={panel.id}
              className="bd-panel"
              type="button"
              tabIndex={0}
            >
              {/* Backdrop \u2014 video if available, otherwise styled placeholder */}
              <div className="bd-panel-bg">
                {panel.video ? (
                  <video
                    ref={(el) => {
                      videoRefs.current[panel.id] = el;
                    }}
                    className="bd-panel-video"
                    src={panel.video}
                    poster={panel.poster}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    aria-hidden="true"
                  />
                ) : (
                  <ImagePlaceholder
                    aspect="3/4"
                    tag={panel.imageTag}
                    description={panel.imagePrompt}
                    dark
                  />
                )}
              </div>

              {/* Dark scrim \u2014 heavier when at rest, lighter on hover */}
              <div className="bd-panel-scrim" />

              {/* Centered label visible at rest */}
              <span className="bd-panel-label-center display">
                {panel.label}
              </span>

              {/* Active state content */}
              <div className="bd-panel-content">
                <span className="bd-panel-label-tag display-italic">
                  {panel.label}
                </span>

                <div className="bd-panel-detail">
                  <h3 className="bd-panel-promise display-italic">
                    {panel.promise}
                  </h3>
                  <p className="bd-panel-body">{panel.body}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default BuiltDifferent;
